/**
 * AccountSwitchedNotice — one-time modal shown after the
 * `linkAnonymousToGoogle` fallback signs the user into a *different*
 * existing account (because the Google account they picked was already
 * linked to a separate Firebase user).
 *
 * Firebase can't merge two Auth users, so any data the guest created
 * during this session stays orphaned under the discarded anon UID. This
 * modal makes that explicit so users aren't confused about missing data.
 *
 * Triggered by `sessionStorage.onyxlife_account_switched = "1"`, set
 * inside UserContext.linkAnonymousToGoogle's catch block.
 */
import React, { useEffect, useState } from "react";
import { useTheme } from "../contexts/ThemeSelection";
import useAccentColor from "../hooks/useAccentColor";
import ThemedButton from "./ThemedButton";

const FLAG_KEY = "onyxlife_account_switched";

const AccountSwitchedNotice: React.FC = () => {
  const { activeTheme, theme } = useTheme();
  const tc = theme[activeTheme];
  const { accent: accentColor } = useAccentColor();

  const [show, setShow] = useState<boolean>(false);

  useEffect(() => {
    // Check once on mount; the flag is set during the auth flow before any
    // navigation, so it'll always be present by the time this component renders.
    if (sessionStorage.getItem(FLAG_KEY) === "1") {
      setShow(true);
      sessionStorage.removeItem(FLAG_KEY);
    }
  }, []);

  if (!show) return null;

  const mutedColor = `${tc.color}99`;

  return (
    <div
      onClick={() => setShow(false)}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.55)",
        zIndex: 11500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          backgroundColor: tc.panelColor,
          borderRadius: 18,
          border: `1px solid ${accentColor}33`,
          padding: 24,
          color: tc.color,
          boxShadow: "0 20px 48px rgba(0,0,0,0.3)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: `${accentColor}22`,
              color: accentColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <i className="fa-solid fa-circle-info" />
          </div>
          <h2 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 700 }}>
            Welcome back
          </h2>
        </div>

        <p style={{ color: mutedColor, fontSize: "0.95rem", lineHeight: 1.4, marginBottom: 8 }}>
          You already have an OnyxLife account with this Google sign-in, so we&apos;ve logged you back into it.
        </p>
        <p style={{ color: mutedColor, fontSize: "0.9rem", lineHeight: 1.4, marginBottom: 18 }}>
          Anything you set up as a guest in this session won&apos;t carry over — your existing account&apos;s data is safe and unchanged.
        </p>

        <ThemedButton variant="accent" fullWidth onClick={() => setShow(false)}>
          Got it
        </ThemedButton>
      </div>
    </div>
  );
};

export default AccountSwitchedNotice;
